import { useEffect, useRef, useState } from "react";
import "../styles/SearchableDropdown.css";

const SearchableDropdown = ({
  options = [],
  label,
  id,
  selectedVal,
  handleChange,
  showClear = false,
  placeholder = "Search...",
  getOptionLabel,
  renderOption,
  isOptionDisabled,
}) => {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(50);

  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const optionsRef = useRef(null);

  useEffect(() => {
    document.addEventListener("click", handleDocumentClick);
    return () => document.removeEventListener("click", handleDocumentClick);
  }, []);

  // function handleDocumentClick(e) {
  //   const clickedInside =
  //     wrapperRef.current && wrapperRef.current.contains(e.target);
  //   if (clickedInside) {
  //     setIsOpen(true);
  //   } else {
  //     setIsOpen(false);
  //   }
  // }

  function handleDocumentClick(e) {
    const clickedInput = inputRef.current && inputRef.current.contains(e.target);

    if (clickedInput) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }

  const extractLabel = (opt) => {
    try {
      const txt =
        (getOptionLabel ? getOptionLabel(opt) : (label ? opt?.[label] : "")) ?? "";
      return typeof txt === "string" ? txt : String(txt ?? "");
    } catch {
      return label ? String(opt?.[label] ?? "") : "";
    }
  };

  const getDisplayValue = () => {
    if (query) return query;
    if (selectedVal) return extractLabel(selectedVal);
    return "";
  };

  const getVisibleOptions = () => {
    if (!options?.length) return [];

    const source = !query
      ? options
      : options.filter((opt) =>
        extractLabel(opt).toLowerCase().includes(query.toLowerCase())
      );

    return source.slice(0, visibleCount);
  };

  const isSelected = (option) => {
    if (!selectedVal) return false;
    if (id) return option?.[id] === selectedVal?.[id];
    return extractLabel(option) === extractLabel(selectedVal);
  };

  const selectOption = (option) => {
    if (isOptionDisabled?.(option)) return;
    setQuery("");
    handleChange?.(option);
    setIsOpen(false);
  };

  // Infinite scroll handler
  useEffect(() => {
    const el = optionsRef.current;
    if (!el) return;

    function onScroll() {
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 5) {
        // near bottom
        setVisibleCount((prev) => prev + 10); // load 10 more
      }
    }

    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="dropdown" ref={wrapperRef}>
      <div className="control">
        <div className="selected-value">
          <input
            ref={inputRef}
            type="text"
            value={getDisplayValue()}
            name="searchTerm"
            placeholder={placeholder}
            autoComplete="off"
            onChange={(e) => {
              const val = e.target.value;
              setQuery(val);
              setVisibleCount(50); // reset count when typing
              if (val === "") handleChange?.(null);
            }}
            onClick={() => setIsOpen(true)}
          />
        </div>

        {showClear && (selectedVal || query) && (
          <span
            className="clear-icon"
            onClick={() => {
              handleChange?.(null);
              setQuery("");
              setIsOpen(false);
              setVisibleCount(10);
            }}
            role="button"
            aria-label="Clear selection"
            title="Clear"
          >
            ×
          </span>
        )}

        <div className={`arrow ${isOpen ? "open" : ""}`} />
      </div>

      <div
        className={`options ${isOpen ? "open" : ""}`}
        ref={optionsRef}
        style={{ maxHeight: "200px", overflowY: "auto" }} // scrollable
      >
        {getVisibleOptions().length === 0 ? (
          <div className="option no-results">No results found</div>
        ) : (
          <>
            {getVisibleOptions().map((option, index) => {
              const disabled = !!isOptionDisabled?.(option);
              const selected = isSelected(option);
              return (
                <div
                  key={
                    option && id && option[id] !== undefined
                      ? `${id}-${option[id]}`
                      : `${id ?? "opt"}-${index}`
                  }
                  className={[
                    "option",
                    selected ? "selected" : "",
                    disabled ? "disabled" : "",
                  ]
                    .join(" ")
                    .trim()}
                  onClick={() => selectOption(option)}
                  role="option"
                  aria-selected={selected}
                  aria-disabled={disabled}
                  title={!renderOption ? extractLabel(option) : undefined}
                >
                  {renderOption ? renderOption(option) : extractLabel(option)}
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
};

export default SearchableDropdown;
